import React, { FC, useEffect, useState } from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { Button, ListItemText, MenuList } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { languages } from '@/features/language-switcher/config';
import { useCookies } from 'react-cookie';

export const LanguageSwitcher: FC<{
  dark?: boolean;
}> = ({ dark }) => {
  const { t, i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [cookies, setCookie] = useCookies(['hl']);

  useEffect(() => {
    if (cookies.hl) {
      i18n.changeLanguage(cookies.hl);
    } else {
      setCookie('hl', 'en');
    }
  }, [cookies]);

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSwitchLanguage = (code: string) => () => {
    setCookie('hl', code);
    i18n.changeLanguage(code);
    handleClose();
  };

  const currentLanguage = languages.find((item) => item.code === cookies.hl);

  return (
    <div>
      {currentLanguage && (
        <Button
          onClick={handleClick}
          endIcon={<KeyboardArrowDownIcon />}
          disableElevation={true}
          sx={dark ? { borderColor: 'white', color: 'white' } : {}}
          data-testid="language-open-btn"
        >
          <FlagImage language={currentLanguage} />
          <span className="hidden md:inline">
            <Trans i18nKey={currentLanguage.label} />
          </span>
        </Button>
      )}

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        data-testid="language-menu"
        slotProps={{
          root: {
            sx: {
              padding: 0,
              margin: 0,
            },
          },
          paper: {
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 10px 18px rgba(0,0,0,0.15))',
              borderRadius: '0.8em',
              padding: '6px',
              ul: {
                padding: '0',
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
              },
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                left: 21,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          },
        }}
      >
        <MenuList sx={{ width: 150, maxWidth: '100%' }}>
          {languages.map((item) => (
            <MenuItem
              key={item.label}
              onClick={handleSwitchLanguage(item.code)}
              sx={{
                borderRadius: '8px',
                padding: '4px 6px',
                minHeight: 'unset',
              }}
            >
              <ListItemText>
                <FlagImage language={item} />
                <span>{t(item.label)}</span>
              </ListItemText>
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    </div>
  );
};

const FlagImage = ({
  language,
}: {
  language: {
    label: string;
    code: string;
    flag: string;
  };
}) => {
  return (
    <img
      src={`https://flagsapi.com/${language.flag.toUpperCase()}/shiny/32.png`}
      alt={language.code.toLowerCase()}
      loading="lazy"
      className="inline mr-2 rounded-xl"
      onError={(e) => {
        e.currentTarget.onerror = null; // prevent looping
        e.currentTarget.src = `/flags/${language.flag.toLowerCase()}.png`;
        e.currentTarget.classList.add('border', 'w-6');
      }}
    />
  );
};
